import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ROLE_META, normalizeRole } from "@/lib/team-roles";
import { SEO } from "@/components/seo/SEO";

type InvitePreview = {
  email: string;
  role: string;
  teamName: string;
  alreadyAccepted: boolean;
};

const AcceptInvite = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";

  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    const run = async () => {
      if (!token) {
        setError("This invitation link is missing its code.");
        setLoading(false);
        return;
      }
      const { data: { session } } = await supabase.auth.getSession();
      setSignedIn(!!session);

      const { data, error: fnError } = await supabase.functions.invoke("accept-invitation", {
        body: { token, action: "preview" },
      });

      if (fnError || !data?.success) {
        setError(data?.error || "This invitation link is no longer valid.");
      } else {
        setPreview(data as InvitePreview);
      }
      setLoading(false);
    };
    run();
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    const { data, error: fnError } = await supabase.functions.invoke("accept-invitation", {
      body: { token },
    });
    setAccepting(false);

    if (fnError || !data?.success) {
      toast.error(data?.error || "Could not accept the invitation.");
      return;
    }
    toast.success(`You've joined ${data.teamName}`);
    navigate("/team-management");
  };

  const goSignIn = () => {
    navigate(`/auth?next=${encodeURIComponent(`/accept-invite?token=${token}`)}`);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <SEO title="Accept team invitation | Veylo AI" description="Join a Veylo AI workspace you were invited to." path="/accept-invite" />
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Users className="h-6 w-6 text-primary" />
          </div>
          <CardTitle>Team invitation</CardTitle>
          <CardDescription>
            {loading ? "Checking your invitation…" : error ? "We couldn't open this invitation" : "You've been invited to join a workspace"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading && (
            <div className="flex justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {!loading && error && (
            <>
              <p className="text-sm text-muted-foreground text-center">{error}</p>
              <Button className="w-full" onClick={() => navigate("/")}>Go to the app</Button>
            </>
          )}

          {!loading && preview && (
            <>
              <div className="rounded-lg border p-4 space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Workspace</span>
                  <span className="font-medium">{preview.teamName}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Invited address</span>
                  <span className="font-medium">{preview.email}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Access level</span>
                  <Badge variant="outline" className={ROLE_META[normalizeRole(preview.role)].badgeClass}>
                    {ROLE_META[normalizeRole(preview.role)].label}
                  </Badge>
                </div>
              </div>

              {signedIn ? (
                <Button className="w-full" onClick={accept} disabled={accepting}>
                  {accepting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Accept invitation
                </Button>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground text-center">
                    Sign in or create your account with <strong>{preview.email}</strong> to join.
                  </p>
                  <Button className="w-full" onClick={goSignIn}>Continue</Button>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptInvite;
